import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import type { SettingKey, SettingValueTypes } from '@/types/settings'

// Type for session overrides storage
export type SessionSettings = {
  [K in SettingKey]?: SettingValueTypes[K]
}

// Context type
export interface SettingsContextType {
  // Session overrides (per-tab, resets on refresh)
  sessionSettings: SessionSettings
  setSessionSetting: <K extends SettingKey>(key: K, value: SettingValueTypes[K]) => void
  getSessionSetting: <K extends SettingKey>(key: K) => SettingValueTypes[K] | undefined
  clearSessionSetting: (key: SettingKey) => void
  clearAllSessionSettings: () => void
}

// Create context with default values
const SettingsContext = createContext<SettingsContextType | null>(null)

// Provider component
export function SettingsProvider({ children }: { children: ReactNode }) {
  const [sessionSettings, setSessionSettings] = useState<SessionSettings>({})

  const setSessionSetting = useCallback(<K extends SettingKey>(key: K, value: SettingValueTypes[K]) => {
    setSessionSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  const getSessionSetting = useCallback(<K extends SettingKey>(key: K): SettingValueTypes[K] | undefined => {
    return sessionSettings[key] as SettingValueTypes[K] | undefined
  }, [sessionSettings])

  const clearSessionSetting = useCallback((key: SettingKey) => {
    setSessionSettings((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const clearAllSessionSettings = useCallback(() => {
    setSessionSettings({})
  }, [])

  const value: SettingsContextType = {
    sessionSettings,
    setSessionSetting,
    getSessionSetting,
    clearSessionSetting,
    clearAllSessionSettings,
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}

// Hook to use the settings context
export function useSettingsContext(): SettingsContextType {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider')
  }
  return context
}
